/**
 * YouTube helpers shared by the panel (paste a link → id + thumbnail) and the site (lite player,
 * oEmbed at build time). The parser mirrors media.youtube_id() in SQL; both run the same cases
 * (db/tests/01_youtube_ids.sql and youtube.test.ts).
 */

const ID = "([A-Za-z0-9_-]{11})";
const PATTERNS = [
  new RegExp(`^(?:https?://)?(?:www\\.|m\\.|music\\.)?youtube\\.com/watch/?\\?(?:[^#]*&)?v=${ID}(?:[&#]|$)`),
  new RegExp(`^(?:https?://)?(?:www\\.)?youtu\\.be/${ID}(?:[?&#/]|$)`),
  new RegExp(`^(?:https?://)?(?:www\\.|m\\.)?youtube(?:-nocookie)?\\.com/(?:embed|shorts|live|v)/${ID}(?:[?&#/]|$)`),
];

/** 11-character video id, or null when the URL is not a YouTube video. */
export function youtubeId(url: string | null | undefined): string | null {
  const u = (url ?? "").trim();
  if (!u) return null;
  for (const re of PATTERNS) {
    const m = re.exec(u);
    if (m?.[1]) return m[1];
  }
  return null;
}

/** Any YouTube address (video, channel, playlist). */
export function isYoutubeUrl(url: string | null | undefined): boolean {
  return /^(https?:\/\/)?([a-z0-9-]+\.)?(youtube\.com|youtu\.be|youtube-nocookie\.com)(\/|$)/i.test((url ?? "").trim());
}

export function isValidYoutubeId(id: string | null | undefined): id is string {
  return typeof id === "string" && /^[A-Za-z0-9_-]{11}$/.test(id);
}

export type ThumbSize = "mq" | "hq" | "sd" | "maxres";

/** Static thumbnail on i.ytimg.com (no cookies, no script). hq = 480×360. */
export function thumbnailUrl(id: string, size: ThumbSize = "hq"): string {
  return `https://i.ytimg.com/vi/${encodeURIComponent(id)}/${size}default.jpg`;
}

/** Privacy-enhanced embed, autoplay because it is only loaded after a click. */
export function embedUrl(id: string, opts: { autoplay?: boolean; start?: number } = {}): string {
  const q = new URLSearchParams({ rel: "0", modestbranding: "1", playsinline: "1" });
  if (opts.autoplay ?? true) q.set("autoplay", "1");
  if (opts.start && opts.start > 0) q.set("start", String(Math.floor(opts.start)));
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?${q.toString()}`;
}

export function watchUrl(id: string): string {
  return `https://www.youtube.com/watch?v=${encodeURIComponent(id)}`;
}

/** Link normalised the same way as media.normalize_url(). */
export function normalizeUrl(url: string | null | undefined): string | null {
  const u = (url ?? "").trim();
  if (!u) return null;
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}

export function oembedUrl(videoUrl: string): string {
  return `https://www.youtube.com/oembed?url=${encodeURIComponent(videoUrl)}&format=json`;
}

export interface Oembed {
  title: string;
  author_name: string | null;
  author_url: string | null;
  thumbnail_url: string | null;
  thumbnail_width: number | null;
  thumbnail_height: number | null;
}

/**
 * Public metadata from YouTube oEmbed (no API key). Returns null on any failure (network, timeout,
 * private or removed video), so callers fall back to what is stored in the database.
 */
export async function fetchOembed(
  videoUrl: string,
  opts: { fetch?: typeof fetch; timeoutMs?: number } = {},
): Promise<Oembed | null> {
  const doFetch = opts.fetch ?? globalThis.fetch;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? 4000);
  try {
    const res = await doFetch(oembedUrl(videoUrl), { signal: ctrl.signal, headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    const d = (await res.json()) as Record<string, unknown>;
    if (typeof d.title !== "string" || !d.title.trim()) return null;
    const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
    const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
    return {
      title: d.title.trim(),
      author_name: str(d.author_name),
      author_url: str(d.author_url),
      thumbnail_url: str(d.thumbnail_url),
      thumbnail_width: num(d.thumbnail_width),
      thumbnail_height: num(d.thumbnail_height),
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export type MediaKind = "interview" | "podcast" | "talk" | "press" | "video" | "live";

export const KIND_LABELS: Record<MediaKind, { one: string; many: string }> = {
  interview: { one: "Entrevista", many: "Entrevistas" },
  podcast: { one: "Podcast", many: "Podcasts" },
  talk: { one: "Palestra gravada", many: "Palestras gravadas" },
  press: { one: "Na imprensa", many: "Na imprensa" },
  video: { one: "Vídeo", many: "Vídeos" },
  live: { one: "Live", many: "Lives" },
};
export const KINDS = Object.keys(KIND_LABELS) as MediaKind[];

/** 1:02:03 or 12:05 (no hours when under one). */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null;
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/** ISO 8601 duration for schema.org (PT1H2M3S). */
export function isoDuration(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null;
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return `PT${h ? `${h}H` : ""}${m ? `${m}M` : ""}${r || (!h && !m) ? `${r}S` : ""}`;
}
